import React from 'react';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Linkedin, 
  Twitter, 
  Facebook,
  ArrowUp
} from 'lucide-react';
import { companyInfo } from '../mock';
import { Link } from 'react-router-dom';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Accueil', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'À Propos', href: '/a-propos' },
    { name: 'Projets', href: '/projets' },
    { name: 'Contact', href: '/contact' }
  ];

  const services = [
    'Audit QoS Réseaux',
    'Ingénierie Radio',
    'Infrastructure IT',
    'Formation Télécoms',
    'Conseil Stratégique',
    'Digitalisation'
  ];

  return (
    <footer className="bg-slate-900 text-white" role="contentinfo" itemScope itemType="https://schema.org/Organization">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1 space-y-6">
            <div className="flex items-center space-x-3">
              <img 
                src={companyInfo.logo} 
                alt={`${companyInfo.name} - Logo entreprise télécommunications et IT Côte d'Ivoire`}
                title={companyInfo.name}
                itemProp="logo"
                className="h-12 w-auto"
              />
              <div>
                <h3 className="text-xl font-bold" itemProp="name">{companyInfo.name}</h3>
                <p className="text-xs text-slate-400" itemProp="alternateName">{companyInfo.fullName}</p>
              </div>
            </div>
            
            <p className="text-slate-300 leading-relaxed" itemProp="description">
              {companyInfo.tagline}. Votre partenaire de confiance pour 
              l'excellence en télécommunications et technologies.
            </p>
            
            <div className="flex space-x-4">
              {/* Réseaux sociaux - À activer ultérieurement
              <a 
                href="https://linkedin.com/company/set-g" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Suivez SET-G sur LinkedIn"
                className="text-slate-400 hover:text-white hover:bg-slate-800 p-2 rounded-md transition-colors"
              >
                <Linkedin className="w-5 h-5" aria-hidden="true" />
              </a>
              <a 
                href="https://twitter.com/setg_ci" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Suivez SET-G sur Twitter"
                className="text-slate-400 hover:text-white hover:bg-slate-800 p-2 rounded-md transition-colors"
              >
                <Twitter className="w-5 h-5" aria-hidden="true" />
              </a>
              <a 
                href="https://facebook.com/setg.ci" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Suivez SET-G sur Facebook"
                className="text-slate-400 hover:text-white hover:bg-slate-800 p-2 rounded-md transition-colors"
              >
                <Facebook className="w-5 h-5" aria-hidden="true" />
              </a>
              */}
            </div>
          </div>
          
          {/* Quick Links */}
          <nav aria-label="Navigation du pied de page">
            <h4 className="text-lg font-semibold mb-6">Navigation</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-slate-300 hover:text-orange-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          
          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Nos Services</h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <Link to="/services" className="text-slate-300 hover:text-orange-400 transition-colors">
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Contact Info */}
          <div itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
            <h4 className="text-lg font-semibold mb-6">Contact</h4>
            <address className="space-y-4 not-italic">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <div className="text-slate-300">
                  <p itemProp="streetAddress">Angré Face Soleil 3, immeuble restaurant Wallace</p>
                  <p><span itemProp="addressLocality">Abidjan Cocody</span>, <span itemProp="addressCountry">Côte d'Ivoire</span></p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-orange-400" aria-hidden="true" />
                <div className="text-slate-300">
                  <a href="tel:+22527223498015" className="hover:text-orange-400 transition-colors block" itemProp="telephone">
                    +225 27 22 34 98 15
                  </a>
                  <a href="tel:+2250757207319" className="hover:text-orange-400 transition-colors block">
                    +225 07 57 20 73 19
                  </a>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-orange-400" aria-hidden="true" />
                <a href="mailto:contact@set-g.com" className="text-slate-300 hover:text-orange-400 transition-colors" itemProp="email">
                  contact@set-g.com
                </a>
              </div>
              
              <div className="flex items-center space-x-3">
                <Globe className="w-5 h-5 text-orange-400" aria-hidden="true" />
                <a href="https://www.set-g.com" target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-orange-400 transition-colors" itemProp="url">
                  www.set-g.com
                </a>
              </div>
            </address>
            
            {/* CTA */}
            <div className="mt-6 p-4 bg-slate-800 rounded-lg">
              <h5 className="font-semibold mb-2">Devis Personnalisé</h5>
              <p className="text-sm text-slate-300 mb-3">
                Obtenez un devis adapté à vos besoins.
              </p>
              <Button 
                asChild
                size="sm"
                className="w-full bg-orange-600 hover:bg-orange-700 text-white"
              >
                <Link to="/contact">
                  Demander un devis
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <Separator className="bg-slate-700" />
      
      {/* Bottom Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-slate-400 text-sm text-center md:text-left">
            <p>
              © {currentYear} {companyInfo.name}. Tous droits réservés.
            </p>
            <p className="mt-1">
              Conçu avec excellence pour l'Afrique digitale.
            </p>
          </div>
          
          <div className="flex items-center space-x-6">
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white"
              onClick={scrollToTop}
              aria-label="Retour en haut de la page"
            >
              <ArrowUp className="w-4 h-4 mr-2" aria-hidden="true" />
              Haut de page
            </Button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;